import { hrefFor } from "./route";
import { INCIDENT_LABEL, formatWhen } from "./presentation";
import type { Incident } from "./types";

type Props = {
  incident: Incident;
  /** Link the title through to the incident's own page. */
  linkToDetail?: boolean;
  /** Show only the latest update — used for resolved history, where the full
   *  timeline is noise until someone asks for it. */
  collapsed?: boolean;
  /** The incident's own page renders the title as its <h1>; repeating it here
   *  would announce the same heading twice to a screen reader. */
  showTitle?: boolean;
};

/** One incident with its update timeline, newest first. Shared by the status
 *  page, the incident list and the incident detail page so the three can never
 *  drift apart. */
export function IncidentCard({ incident, linkToDetail, collapsed, showTitle = true }: Props) {
  // Newest first: during an incident the current state is what people came for,
  // and making them read to the bottom to find it is a poor trade.
  const updates = [...incident.updates].reverse();
  const shown = collapsed ? updates.slice(0, 1) : updates;

  const meta = [
    incident.reference,
    `started ${formatWhen(incident.started_at)}`,
    incident.resolved_at ? `resolved ${formatWhen(incident.resolved_at)}` : null,
    incident.services.length ? incident.services.join(", ") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <article className="status-incident">
      {showTitle && (
        <h3>
          {linkToDetail ? (
            <a href={hrefFor(`/status/incidents/${incident.reference}`)}>{incident.title}</a>
          ) : (
            incident.title
          )}
        </h3>
      )}
      <div className="status-incident__ref">{meta}</div>

      {shown.length > 0 && (
        <ul className="status-timeline">
          {shown.map((u, i) => (
            <li key={`${u.created_at}-${i}`}>
              <div className="status-timeline__status">
                {INCIDENT_LABEL[u.status] ?? u.status}
              </div>
              <p className="status-timeline__msg">{u.message}</p>
              <div className="status-timeline__when">{formatWhen(u.created_at)}</div>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
